import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import "./styles.css";

const TYPES = [
  ["text", "Texto curto"],
  ["textarea", "Texto longo"],
  ["email", "E-mail"],
  ["phone", "Telefone"],
  ["cpf", "CPF"],
  ["cnpj", "CNPJ"],
  ["date", "Data"],
  ["select", "Lista de opções"],
  ["radio", "Escolha única"],
  ["multiselect", "Múltipla escolha"],
  ["checkbox", "Confirmação"]
];

const CHOICES = ["select", "radio", "multiselect"];

const createField = (type = "text", label = "Nova pergunta") => ({
  key: `campo_${Date.now().toString(36)}_${Math.random()
    .toString(36).slice(2, 7)}`,
  label,
  type,
  required: false,
  placeholder: "",
  helpText: "",
  options: CHOICES.includes(type)
    ? ["Opção 1", "Opção 2"]
    : []
});

const newForm = () => ({
  id: null,
  title: "",
  description: "",
  status: "draft",
  fields: [
    { key: "nome", label: "Nome completo", type: "text",
      required: true, placeholder: "Digite seu nome",
      helpText: "", options: [] },
    { key: "email", label: "E-mail", type: "email",
      required: true, placeholder: "nome@exemplo.com",
      helpText: "", options: [] }
  ]
});

const typeName = type =>
  TYPES.find(item => item[0] === type)?.[1] || type;

const errorMessage = err =>
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  "Não foi possível concluir a operação.";

function PreviewField({ field }) {
  const options = Array.isArray(field.options)
    ? field.options.filter(Boolean)
    : [];

  return (
    <div className="kf-preview-field">
      <label>
        {field.label || "Pergunta sem título"}
        {field.required && <em> *</em>}
      </label>

      {field.helpText && (
        <p className="kf-hint">{field.helpText}</p>
      )}

      {field.type === "textarea" ? (
        <textarea disabled rows={3}
          placeholder={field.placeholder || "Sua resposta"} />
      ) : field.type === "select" ? (
        <select disabled defaultValue="">
          <option value="">Selecione uma opção</option>
          {options.map((option, i) => (
            <option key={i}>{option}</option>
          ))}
        </select>
      ) : field.type === "radio" ||
          field.type === "multiselect" ? (
        <div className="kf-preview-options">
          {options.map((option, i) => (
            <label key={i}>
              <input disabled
                type={field.type === "radio"
                  ? "radio" : "checkbox"} />
              {option}
            </label>
          ))}
        </div>
      ) : field.type === "checkbox" ? (
        <label className="kf-preview-check">
          <input type="checkbox" disabled />
          Confirmar
        </label>
      ) : (
        <input disabled
          type={field.type === "email" ? "email"
            : field.type === "date" ? "date" : "text"}
          placeholder={field.placeholder || "Sua resposta"} />
      )}
    </div>
  );
}

export default function KoraxForms() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [search, setSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(true);

  const loadForms = async () => {
    setLoading(true);
    try {
      const response = await api.get("/korax-forms");
      setForms(Array.isArray(response.data?.forms)
        ? response.data.forms : []);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  const filtered = useMemo(() =>
    forms.filter(form =>
      (form.title || "").toLowerCase()
        .includes(search.toLowerCase().trim())
    ), [forms, search]
  );

  const patch = changes =>
    setEditor(prev => ({ ...prev, ...changes }));

  const patchField = (key, changes) =>
    setEditor(prev => ({
      ...prev,
      fields: prev.fields.map(field =>
        field.key === key
          ? { ...field, ...changes }
          : field
      )
    }));

  const addField = type =>
    setEditor(prev => ({
      ...prev,
      fields: [...prev.fields, createField(type)]
    }));

  const removeField = key =>
    setEditor(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.key !== key)
    }));

  const moveField = (index, delta) =>
    setEditor(prev => {
      const next = [...prev.fields];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [
        next[target], next[index]
      ];
      return { ...prev, fields: next };
    });

  const save = async event => {
    event.preventDefault();
    if (saving) return;

    const title = editor.title.trim();

    if (title.length < 3) {
      toast.warn("Informe um título com pelo menos 3 caracteres.");
      return;
    }

    if (editor.fields.length < 1 ||
        editor.fields.length > 40) {
      toast.warn("Use entre 1 e 40 perguntas.");
      return;
    }

    const fields = editor.fields.map(field => ({
      key: field.key,
      label: field.label.trim(),
      type: field.type,
      required: Boolean(field.required),
      placeholder: (field.placeholder || "").trim(),
      helpText: (field.helpText || "").trim(),
      ...(CHOICES.includes(field.type)
        ? {
            options: (field.options || [])
              .map(option => option.trim())
              .filter(Boolean)
          }
        : {})
    }));

    for (const field of fields) {
      if (field.label.length < 2) {
        toast.warn("Todas as perguntas precisam de um título.");
        return;
      }
      if (CHOICES.includes(field.type) &&
          (field.options || []).length < 2) {
        toast.warn(
          `Adicione ao menos duas opções em "${field.label}".`
        );
        return;
      }
    }

    setSaving(true);
    const payload = {
      title,
      description: editor.description.trim(),
      status: editor.status,
      fields
    };

    try {
      if (editor.id) {
        await api.put(`/korax-forms/${editor.id}`, payload);
        toast.success("Formulário atualizado.");
      } else {
        await api.post("/korax-forms", payload);
        toast.success("Formulário criado.");
      }

      setEditor(null);
      await loadForms();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="kf-root">
      <div className="kf-container">
        <header className="kf-heading">
          <div>
            <span className="kf-eyebrow">
              ATENDIMENTO · FORMULÁRIOS
            </span>
            <h1>Formulários</h1>
            <p>Crie experiências simples para coletar
              informações dos seus clientes.</p>
          </div>
          <button className="kf-btn kf-primary"
            type="button"
            onClick={() => setEditor(newForm())}>
            <span aria-hidden="true">＋</span> Novo formulário
          </button>
        </header>

        {!editor ? (
          <>
            <div className="kf-toolbar">
              <div>
                <h2>Biblioteca de modelos</h2>
                <p>Compartilhada com os usuários da sua empresa.</p>
              </div>
              <input
                type="search"
                aria-label="Buscar formulários"
                placeholder="Buscar formulário..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="kf-empty">Carregando formulários...</div>
            ) : filtered.length === 0 ? (
              <div className="kf-empty">
                <div className="kf-empty-symbol">▤</div>
                <h3>
                  {search ? "Nenhum formulário encontrado"
                    : "Seus formulários começam aqui"}
                </h3>
                <p>
                  {search
                    ? "Tente buscar por outro nome."
                    : "Monte um modelo reutilizável para sua equipe."}
                </p>
                {!search && (
                  <button type="button"
                    className="kf-btn kf-primary"
                    onClick={() => setEditor(newForm())}>
                    Criar primeiro formulário
                  </button>
                )}
              </div>
            ) : (
              <div className="kf-library">
                {filtered.map(form => (
                  <article className="kf-model" key={form.id}>
                    <div className="kf-model-top">
                      <div className="kf-model-icon">▤</div>
                      <span className={
                        `kf-status kf-${form.status}`
                      }>
                        {form.status === "active"
                          ? "Ativo"
                          : form.status === "archived"
                            ? "Arquivado" : "Rascunho"}
                      </span>
                    </div>
                    <h3>{form.title}</h3>
                    <p>{form.description ||
                      "Formulário personalizado"}</p>
                    <div className="kf-model-bottom">
                      <span>
                        {form.fields?.length || 0}
                        {" "}perguntas
                      </span>
                      <button type="button"
                        className="kf-btn kf-secondary"
                        onClick={() => {
                          if (form.status === "archived") {
                            toast.info(
                              "Este formulário está arquivado."
                            );
                            return;
                          }
                          setEditor({
                            ...form,
                            fields: form.fields.map(field => ({
                              ...field,
                              options: Array.isArray(field.options)
                                ? [...field.options] : []
                            }))
                          });
                        }}>
                        {form.status === "archived"
                          ? "Indisponível" : "Editar modelo"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="kf-editor-bar">
              <button type="button"
                className="kf-back"
                onClick={() => setEditor(null)}>
                ← Voltar à biblioteca
              </button>
              <span>
                {editor.id ? "Editar formulário"
                  : "Novo formulário"}
              </span>
            </div>

            <form onSubmit={save}>
              <div className="kf-editor-grid">
                <section className="kf-editor">
                  <div className="kf-panel">
                    <div className="kf-panel-heading">
                      <span className="kf-step">01</span>
                      <div>
                        <h2>Informações gerais</h2>
                        <p>O cliente verá estas informações
                          antes de responder.</p>
                      </div>
                    </div>

                    <div className="kf-control">
                      <label htmlFor="kf-title">
                        Nome do formulário *
                      </label>
                      <input id="kf-title" required
                        minLength={3}
                        maxLength={180}
                        value={editor.title}
                        onChange={e => patch({
                          title: e.target.value
                        })}
                        placeholder="Ex.: Cadastro de cliente" />
                    </div>

                    <div className="kf-control">
                      <label htmlFor="kf-description">
                        Descrição
                      </label>
                      <textarea id="kf-description"
                        rows={3}
                        maxLength={1200}
                        value={editor.description}
                        onChange={e => patch({
                          description: e.target.value
                        })}
                        placeholder="Explique brevemente o objetivo." />
                    </div>

                    <div className="kf-control">
                      <label htmlFor="kf-status">
                        Disponibilidade
                      </label>
                      <select id="kf-status"
                        value={editor.status}
                        onChange={e => patch({
                          status: e.target.value
                        })}>
                        <option value="draft">
                          Rascunho
                        </option>
                        <option value="active">
                          Ativo para envio
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="kf-panel">
                    <div className="kf-panel-heading">
                      <span className="kf-step">02</span>
                      <div>
                        <h2>Perguntas</h2>
                        <p>Organize os campos do jeito que
                          sua empresa precisa.</p>
                      </div>
                      <span className="kf-count">
                        {editor.fields.length}/40
                      </span>
                    </div>

                    <div className="kf-fields">
                      {editor.fields.map((field, index) => (
                        <article className="kf-field" key={field.key}>
                          <div className="kf-field-top">
                            <strong>
                              {String(index + 1).padStart(2, "0")}
                              {" "}· {typeName(field.type)}
                            </strong>
                            <div className="kf-field-actions">
                              <button type="button"
                                aria-label="Mover para cima"
                                disabled={index === 0}
                                onClick={() => moveField(index, -1)}>
                                ↑
                              </button>
                              <button type="button"
                                aria-label="Mover para baixo"
                                disabled={index ===
                                  editor.fields.length - 1}
                                onClick={() => moveField(index, 1)}>
                                ↓
                              </button>
                              <button type="button"
                                className="kf-remove"
                                aria-label="Remover pergunta"
                                onClick={() => removeField(field.key)}>
                                ×
                              </button>
                            </div>
                          </div>

                          <div className="kf-two-columns">
                            <div className="kf-control">
                              <label>Pergunta *</label>
                              <input required maxLength={150}
                                value={field.label}
                                onChange={e => patchField(
                                  field.key,
                                  { label: e.target.value }
                                )} />
                            </div>

                            <div className="kf-control">
                              <label>Tipo de resposta</label>
                              <select value={field.type}
                                onChange={e => {
                                  const type = e.target.value;
                                  patchField(field.key, {
                                    type,
                                    options: CHOICES.includes(type)
                                      ? field.options?.length >= 2
                                        ? field.options
                                        : ["Opção 1", "Opção 2"]
                                      : []
                                  });
                                }}>
                                {TYPES.map(([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {CHOICES.includes(field.type) ? (
                            <div className="kf-control">
                              <label>Opções (uma por linha)</label>
                              <textarea rows={3}
                                value={(field.options || []).join("\n")}
                                onChange={e => patchField(
                                  field.key,
                                  { options: e.target.value.split("\n") }
                                )}
                                placeholder={"Opção 1\nOpção 2"} />
                            </div>
                          ) : field.type !== "checkbox" ? (
                            <div className="kf-control">
                              <label>Texto de exemplo</label>
                              <input maxLength={180}
                                value={field.placeholder || ""}
                                onChange={e => patchField(
                                  field.key,
                                  { placeholder: e.target.value }
                                )}
                                placeholder="Opcional" />
                            </div>
                          ) : null}

                          <div className="kf-field-footer">
                            <label className="kf-checkbox">
                              <input type="checkbox"
                                checked={Boolean(field.required)}
                                onChange={e => patchField(
                                  field.key,
                                  { required: e.target.checked }
                                )} />
                              Resposta obrigatória
                            </label>
                          </div>
                        </article>
                      ))}
                    </div>

                    <div className="kf-add-wrap">
                      <span>Adicionar pergunta</span>
                      <div className="kf-type-grid">
                        {TYPES.map(([type, label]) => (
                          <button type="button" key={type}
                            disabled={editor.fields.length >= 40}
                            onClick={() => addField(type)}>
                            <span>＋</span>{label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                <aside className="kf-side">
                  <div className="kf-side-heading">
                    <div>
                      <h2>Pré-visualização</h2>
                      <p>Assim seu cliente verá os campos.</p>
                    </div>
                    <button type="button" className="kf-back"
                      onClick={() => setPreviewOpen(!previewOpen)}>
                      {previewOpen ? "Ocultar" : "Mostrar"}
                    </button>
                  </div>

                  {previewOpen && (
                    <div className="kf-preview">
                      <div className="kf-preview-symbol">▤</div>
                      <h3>{editor.title || "Título do formulário"}</h3>
                      <p>{editor.description ||
                        "Preencha as informações abaixo."}</p>
                      <div className="kf-preview-divider" />
                      {editor.fields.map(field => (
                        <PreviewField
                          key={field.key}
                          field={field} />
                      ))}
                      <div className="kf-preview-submit">
                        Enviar respostas →
                      </div>
                      <small>
                        Visualização ilustrativa · Sem envio
                      </small>
                    </div>
                  )}
                </aside>
              </div>

              <div className="kf-savebar">
                <span>
                  As alterações serão compartilhadas com
                  a equipe da empresa.
                </span>
                <div>
                  <button type="button"
                    className="kf-btn kf-secondary"
                    onClick={() => setEditor(null)}>
                    Cancelar
                  </button>
                  <button type="submit"
                    disabled={saving}
                    className="kf-btn kf-primary">
                    {saving ? "Salvando..." : "Salvar formulário"}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
